import { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { supabase } from "utils/supabase";

function safeDecodeNext(nextParam) {
  if (!nextParam) return null;
  try {
    const decoded = decodeURIComponent(nextParam);

    // Seguridad básica: solo paths internos
    if (!decoded.startsWith("/")) return null;
    if (decoded.startsWith("//")) return null;

    return decoded;
  } catch {
    return null;
  }
}

function getDefaultRouteByRole(role) {
  switch (role) {
    case "accountant":
      return "/contabilidad/facturacion";
    case "lab_manager":
      return "/ops/my-pos";
    case "reception":
    case "administrative_assistant":
      return "/ingresos";
    case "technician":
      return "/operations-overview";
    case "admin":
    default:
      return "/"; // client-analytics por tu router
  }
}

function normalizeNextByRole(nextPath, role) {
  if (!nextPath) return null;

  if (role === "accountant" && nextPath.startsWith("/facturacion/laboratorio")) {
    return nextPath.replace("/facturacion/laboratorio", "/contabilidad/facturacion");
  }

  if (
    role === "lab_manager" &&
    (nextPath === "/facturacion" ||
      nextPath.startsWith("/facturacion/") ||
      nextPath.startsWith("/contabilidad/facturacion"))
  ) {
    return "/ops/my-pos";
  }

  return nextPath;
}

export default function Login() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const navigate = useNavigate();
  const location = useLocation();

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    const normalizedEmail = String(email || "").trim().toLowerCase();

    const { error: signErr } = await supabase.auth.signInWithPassword({
      email: normalizedEmail,
      password,
    });

    if (signErr) {
      setError(
        signErr.message === "Invalid login credentials"
          ? "Credenciales invalidas. Verificá el email y la contraseña, y confirmá que el usuario exista en Supabase Auth."
          : signErr.message
      );
      setLoading(false);
      return;
    }

    try {
      // 1) Si viene next=... lo respetamos (link desde mail, etc.)
      const qp = new URLSearchParams(location.search);
      const nextRaw = qp.get("next");
      const nextPath = safeDecodeNext(nextRaw);

      // 2) Buscar el rol del usuario en profiles
      const {
        data: { user },
        error: userErr,
      } = await supabase.auth.getUser();

      if (userErr) throw userErr;

      let role = null;

      if (user?.id) {
        const { data: prof, error: profErr } = await supabase
          .from("profiles")
          .select("role")
          .eq("id", user.id)
          .maybeSingle();

        if (profErr) throw profErr;
        role = prof?.role || null;
      }

      // 3) Redirigir
      if (nextPath) {
        navigate(normalizeNextByRole(nextPath, role), { replace: true });
      } else {
        navigate(getDefaultRouteByRole(role), { replace: true });
      }
    } catch (e2) {
      console.error("[Login] redirect error:", e2);
      // fallback razonable
      navigate("/operations-overview", { replace: true });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="relative min-h-screen w-full flex items-center justify-center overflow-hidden">
      <video
        autoPlay
        loop
        muted
        playsInline
        className="absolute z-0 w-auto min-w-full min-h-full max-w-none"
        style={{
          position: "fixed",
          right: 0,
          bottom: 0,
          objectFit: "cover",
        }}
      >
        <source src="/videos/Login-Page7.mp4" type="video/mp4" />
        Tu navegador no soporta el tag de video.
      </video>

      <div className="relative z-10 w-full max-w-md p-8 space-y-6 bg-white/10 backdrop-blur-md rounded-xl shadow-2xl border border-white/20">
        <div className="text-center">
          <h2 className="text-3xl font-bold text-white">Bienvenido a InstruLab</h2>
          <p className="mt-2 text-gray-200">Ingresa tus credenciales para acceder.</p>
        </div>

        <form onSubmit={handleLogin} className="space-y-4">
          <div>
            <label className="text-sm font-medium text-gray-200">Email</label>
            <input
              type="email"
              className="mt-1 block w-full px-4 py-2 text-gray-800 bg-white/90 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              placeholder="tu@email.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
            />
          </div>

          <div>
            <label className="text-sm font-medium text-gray-200">Contraseña</label>
            <input
              type="password"
              className="mt-1 block w-full px-4 py-2 text-gray-800 bg-white/90 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              placeholder="••••••••"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
            />
          </div>

          {error && (
            <p className="text-sm text-red-300 bg-red-800/50 p-2 rounded-md">{error}</p>
          )}

          <div>
            <button
              type="submit"
              disabled={loading}
              className="w-full px-4 py-2 text-sm font-semibold text-white bg-blue-600 rounded-md shadow-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:bg-gray-400 transition-all duration-300"
            >
              {loading ? "Ingresando..." : "Ingresar"}
            </button>
          </div>
        </form>

        <div className="text-center text-xs text-gray-300">
          <a href="#" className="hover:underline">
            ¿Olvidaste tu contraseña?
          </a>
        </div>
      </div>
    </div>
  );
}
